import React from 'react';
import PropTypes from 'prop-types';
import Dropdown from 'relements/build/esm/input/dropdown';
import Text from 'relements/build/esm/input/text';
import { isProduction } from '@src/utils';

// Components
import Icon from 'components/Icon';

import styles from './PromptTriggerRule.scss';

const parseToType = (variableType, value, supportedTypes) => {
  if (variableType === supportedTypes.number) {
    return parseInt(value, 10);
  }
  return value;
};

export default class PromptTriggerRule extends React.Component {
  render() {
    return (
      <div className={styles.container}>
        <div className={styles.ruleRow}>
          {this.renderVariableSelectionDropdown()}
          {this.renderOperandSelectionDropdown()}
          {this.renderValueInputOption()}
          <div className={styles.actionIcons}>
            <Icon
              iconType="trash"
              onClick={!isProduction() && this.props.onRemove}
            />
          </div>
        </div>
      </div>
    );
  }

  renderVariableSelectionDropdown = () => {
    const { supportedVariables } = this.props;
    const variableOptions = Object.keys(supportedVariables).map((variable) => {
      return {
        name: supportedVariables[variable].label,
        type: supportedVariables[variable].type,
        id: variable,
      };
    });
    const variableValue = variableOptions.filter(
      (option) => option.id === this.props.variable
    )[0];

    return (
      <Dropdown
        disabled={isProduction()}
        withSearch
        options={variableOptions}
        label="Trigger Based On"
        placeholder="Please select the metric to be used"
        optionKey="name"
        value={variableValue}
        onChange={(value) => this.props.onChange('variable', value.id)}
        error={this.props.error.variable}
      />
    );
  };

  renderOperandSelectionDropdown = () => {
    if (!this.props.variable) return null;

    const variableType = this.props.supportedVariables[this.props.variable]
      .type;
    const supportedOperators = this.props.supportedOperatorsByType[
      variableType
    ];

    const operatorOptions = supportedOperators.map((operator) => {
      return {
        name: this.props.supportedOperators[operator],
        id: operator,
      };
    });

    const operatorValue = operatorOptions.filter(
      (option) => option.id === this.props.operator
    )[0];

    return (
      <Dropdown
        disabled={isProduction()}
        withSearch
        options={operatorOptions}
        label="Condition"
        placeholder="Please select the operator to be used"
        optionKey="name"
        value={operatorValue}
        onChange={(value) => this._onOperatorChanged(value.id)}
        error={this.props.error.operator}
      />
    );
  };

  renderValueInputOption = () => {
    if (!this.props.variable) return null;
    const variable = this.props.supportedVariables[this.props.variable];
    const metaErrors = this.props.error.meta || {};
    return (
      <div className={styles.valueColumn}>
        {!this.props.fixedValueOperators[this.props.operator] && (
          <Text
            disabled={isProduction()}
            min={0}
            label="Value"
            placeholder="Value to be used for Comparison"
            value={String(this.props.value)}
            inputType={variable.type}
            onChange={(value) => {
              this.props.onChange(
                'value',
                parseToType(variable.type, value, this.props.supportedTypes)
              );
            }}
            error={Boolean(this.props.error.value)}
            errorMessage={this.props.error.value}
          />
        )}
        {variable.metaKeys.map((metaKey) => {
          return (
            <Text
              disabled={isProduction()}
              min={0}
              label={metaKey.title}
              placeholder="Additional meta fields for the rule"
              value={String(this.props.meta[metaKey.key] ?? '')}
              inputType={metaKey.type}
              onChange={(value) =>
                this._onMetaFieldChanged(
                  metaKey.key,
                  parseToType(metaKey.type, value, this.props.supportedTypes)
                )
              }
              error={Boolean(metaErrors[metaKey.key])}
              errorMessage={metaErrors[metaKey.key]}
            />
          );
        })}
      </div>
    );
  };

  _onOperatorChanged = (operatorId) => {
    this.props.onChange('operator', operatorId);
    if (this.props.fixedValueOperators[operatorId]) {
      this.props.onChange(
        'value',
        this.props.fixedValueOperators[operatorId].value
      );
    }
  };

  _onMetaFieldChanged = (metaKey, value) => {
    const meta = { ...this.props.meta };
    meta[metaKey] = value;
    this.props.onChange('meta', meta);
  };
}

PromptTriggerRule.propTypes = {
  fixedValueOperators: PropTypes.object,
  supportedOperators: PropTypes.object,
  supportedOperatorsByType: PropTypes.object,
  supportedVariables: PropTypes.object,
  supportedTypes: PropTypes.object,
  variable: PropTypes.string,
  operator: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  meta: PropTypes.object,
  onRemove: PropTypes.func,
  onChange: PropTypes.func,
  error: PropTypes.shape({
    variable: PropTypes.string,
    operator: PropTypes.string,
    value: PropTypes.string,
    meta: PropTypes.object,
  }),
};

PromptTriggerRule.defaultProps = {
  variable: '',
  operator: '',
  value: '',
  meta: {},
  error: {
    variable: '',
    operator: '',
    value: '',
    meta: {},
  },
  onRemove: () => {},
  onChange: () => {},
};
